import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { Visibility } from "@mui/icons-material";
import apiClient from "../utils/apiClient";
import { toast } from "react-toastify";

const AdminReviewList = () => {
  const [reviews, setReviews] = useState([]);
  const [search, setSearch] = useState("");
  const [approveFilter, setApproveFilter] = useState("");
  const [replyFilter, setReplyFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);

  const [statusDialog, setStatusDialog] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");

  const approvalColor = {
    pending: "warning",
    approved: "success",
    rejected: "error",
  };

  // 🔥 Fetch Reviews (ADMIN API)
  const fetchReviews = async () => {
    try {
      setLoading(true);

      const res = await apiClient.get("/api/admin/review", {
        params: {
          search,
          approvalStatus: approveFilter,
          isReply: replyFilter,
          page: page + 1,
          limit: rowsPerPage,
        },
      });

      setReviews(res?.data?.data?.data || []);
      setTotalCount(res?.data?.data?.total || 0);
    } catch (err) {
      toast.error("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [search, approveFilter, replyFilter, page, rowsPerPage]);

  // 👁️ View Dialog
  const handleView = (row) => {
    setSelectedReview(row);
    setOpenDialog(true);
  };

  // 🔥 Status Change Trigger
  const handleStatusChange = (row, value) => {
    setSelectedReview(row);
    setSelectedStatus(value);

    if (value === "rejected") {
      setStatusDialog(true); // open reason dialog
    } else {
      updateStatus(value);
    }
  };

  // 🔥 Update Status API
  const updateStatus = async (status) => {
    if (status === "rejected") {
      if (!rejectionReason) {
        toast.error("Rejected text is required");
        return
      }
    }

    try {
      await apiClient.put(`/api/admin/review/${selectedReview.id}`, {
        approvalStatus: status,
        rejectionReason: status === "rejected" ? rejectionReason : null,
      });

      toast.success("Status updated");
      setStatusDialog(false);
      setRejectionReason("");
      fetchReviews();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      {/* Header */}
      <Typography variant="h5" sx={{ mb: 2 }}>
        Admin Review Management
      </Typography>

      {/* 🔍 Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <TextField
          label="Search"
          size="small"
          sx={{ width: 200 }}

          value={search}
          onChange={(e) => {
            setPage(0);
            setSearch(e.target.value);
          }}
        />

        <TextField
          select
          size="small"
          label="Approval"
          sx={{ width: 200 }}

          value={approveFilter}
          onChange={(e) => {
            setPage(0);
            setApproveFilter(e.target.value);
          }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
        </TextField>

        <TextField
          select
          size="small"
          sx={{ width: 200 }}

          label="Reply Status"
          value={replyFilter}
          onChange={(e) => {
            setPage(0);
            setReplyFilter(e.target.value);
          }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="true">Replied</MenuItem>
          <MenuItem value="false">Not Replied</MenuItem>
        </TextField>
      </Box>

      {/* 📋 Table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Vendor</TableCell>
              <TableCell>Rating</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Reply</TableCell>

              <TableCell>Change</TableCell>
              <TableCell align="center">View</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {reviews.length > 0 ? (
              reviews.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row?.product?.name}</TableCell>
                  <TableCell>{row?.customer?.name}</TableCell>
                  <TableCell>{row?.vendor?.name}</TableCell>

                  <TableCell>
                    <Chip label={`⭐ ${row.rating}`} size="small" />
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={row.approvalStatus}
                      color={approvalColor[row.approvalStatus]}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {row.isReply ? (
                      <Chip label="Replied" size="small" color="success" />
                    ) : (
                      <Chip label="Pending" size="small" color="warning" />
                    )}
                  </TableCell>

                  {/* 🔥 Change Status */}
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={row.approvalStatus}
                      onChange={(e) =>
                        handleStatusChange(row, e.target.value)
                      }
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                    </TextField>
                  </TableCell>

                  <TableCell align="center">
                    <IconButton onClick={() => handleView(row)}>
                      <Visibility />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {loading ? "Loading..." : "No reviews found"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 📄 Pagination */}
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />

      {/* 👁️ View Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth>
        <DialogTitle>Review Details</DialogTitle>
        <DialogContent dividers>
          {selectedReview && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography><b>Product:</b> {selectedReview?.product?.name}</Typography>
              <Typography><b>Customer:</b> {selectedReview?.customer?.name}</Typography>
              <Typography><b>Vendor:</b> {selectedReview?.vendor?.name}</Typography>
              <Typography><b>Rating:</b> ⭐ {selectedReview.rating}</Typography>
              <Typography><b>Review:</b> {selectedReview.comment}</Typography>
              {selectedReview.rejectionReason && <Typography><b>Rejection Reason:</b> {selectedReview.rejectionReason}</Typography>}
           
              <Typography><b>Reply:</b> {selectedReview.vendorReply || "No reply"}</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ❌ Reject Reason Dialog */}
      <Dialog open={statusDialog} onClose={() => setStatusDialog(false)}>
        <DialogTitle>Reject Reason</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Reason"
            fullWidth
            multiline
            rows={3}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => updateStatus("rejected")}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AdminReviewList;